import React, { useState } from 'react';
import './AdditionalCosts.css';
import { formatPLN } from '@repo/domain';

// forma "dostępne: N" — niezależna od rodzaju jednostki (komórka/boks/miejsce)
const formatAvailableCount = (count) => `dostępne: ${count}`;

// Numery jednostek pokazujemy dopiero po kliknięciu — przy długich tytułach
// lista zajmowała więcej miejsca niż sama oferta
function OptionalUnitGroup({ group }) {
  const [showTitles, setShowTitles] = useState(false);

  return (
    <div className="cost-item">
      <div className="cost-description">
        <span className="cost-name">
          {group.propertyTypeLabel}
          {group.optionalUnitPostfix && ` ${group.optionalUnitPostfix}`}
          {group.floorLabel && `, ${group.floorLabel}`}
          {Number.isFinite(group.areaTotal) && `, ${group.areaTotal.toFixed(2).replace('.', ',')} ${group.areaUnit}`}
        </span>
        <button
          type="button"
          className="cost-units-count"
          onClick={() => setShowTitles(!showTitles)}
          aria-expanded={showTitles}
        >
          {formatAvailableCount(group.titles.length)}
          <span className="cost-units-arrow">{showTitles ? '▲' : '▼'}</span>
        </button>
        {showTitles && (
          <ul className="cost-units">
            {group.titles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="cost-amount">
        {formatPLN(group.price)}
      </div>
    </div>
  );
}

function AdditionalCosts({ additionalCosts, propertyPrice, parkingSpots, parkingPrice, parkingIncluded, optionalUnits = [] }) {

  // Group costs by type
  const mandatoryCosts = additionalCosts.filter(cost => !cost.is_optional && !cost.is_included_in_price);
  const optionalCosts = additionalCosts.filter(cost => cost.is_optional && !cost.is_included_in_price);
  const includedCosts = additionalCosts.filter(cost => cost.is_included_in_price);

  // Calculate total mandatory costs
  const totalMandatoryCosts = mandatoryCosts.reduce((sum, cost) => sum + cost.amount, 0);
  const totalWithMandatory = propertyPrice + totalMandatoryCosts;

  return (
    <div className="additional-costs">
      <h3>Dodatkowe koszty zakupu</h3>
      
      {/* Mandatory costs */}
      {mandatoryCosts.length > 0 && (
        <div className="costs-section mandatory-costs">
          <h4>Koszty obowiązkowe</h4>
          <div className="costs-list">
            {mandatoryCosts.map((cost, index) => (
              <div key={index} className="cost-item">
                <div className="cost-description">
                  <span className="cost-name">{cost.description}</span>
                </div>
                <div className="cost-amount">
                  {formatPLN(cost.amount)}
                </div>
              </div>
            ))}
          </div>
          <div className="costs-subtotal">
            <span>Razem koszty obowiązkowe:</span>
            <span className="subtotal-amount">{formatPLN(totalMandatoryCosts)}</span>
          </div>
        </div>
      )}

      {/* Optional costs */}
      {(optionalCosts.length > 0 || (parkingSpots > 0 && !parkingIncluded)) && (
        <div className="costs-section optional-costs">
          <h4>Koszty opcjonalne</h4>
          <div className="costs-list">
            {optionalCosts.map((cost, index) => (
              <div key={index} className="cost-item">
                <div className="cost-description">
                  <span className="cost-name">{cost.description}</span>
                </div>
                <div className="cost-amount">
                  {formatPLN(cost.amount)}
                </div>
              </div>
            ))}
            {parkingSpots > 0 && !parkingIncluded && parkingPrice && (
              <div className="cost-item">
                <div className="cost-description">
                  <span className="cost-name">Miejsce postojowe</span>
                  <span className="cost-units">({parkingSpots} dostępne)</span>
                </div>
                <div className="cost-amount">
                  {formatPLN(parkingPrice)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Optional units section */}
      {optionalUnits.length > 0 && (
        <div className="costs-section optional-units">
          <h4>Jednostki opcjonalne do dokupienia</h4>
          <div className="costs-list">
            {(() => {
              // Group units by type, price, postfix, area and floor
              const grouped = optionalUnits.reduce((acc, unit) => {
                const area = unit.specifications.area;
                const floorLabel = unit.location.floor_label;
                const key = `${unit.propertyType}_${unit.pricing.totalPrice.value}_${unit.optionalUnitPostfix || ''}_${area.total}_${floorLabel}`;
                if (!acc[key]) {
                  acc[key] = {
                    propertyType: unit.propertyType,
                    propertyTypeLabel: unit.propertyTypeLabel,
                    optionalUnitPostfix: unit.optionalUnitPostfix,
                    areaTotal: area.total,
                    areaUnit: area.unit,
                    floorLabel,
                    price: unit.pricing.totalPrice.value,
                    currency: unit.pricing.totalPrice.currency,
                    titles: []
                  };
                }
                acc[key].titles.push(unit.title);
                return acc;
              }, {});

              return Object.values(grouped).map((group, index) => (
                <OptionalUnitGroup key={index} group={group} />
              ));
            })()}
          </div>
        </div>
      )}

      {/* Included costs */}
      {(includedCosts.length > 0 || (parkingSpots > 0 && parkingIncluded)) && (
        <div className="costs-section included-costs">
          <h4>Zawarte w cenie</h4>
          <div className="costs-list">
            {includedCosts.map((cost, index) => (
              <div key={index} className="cost-item included">
                <div className="cost-description">
                  <span className="cost-name">{cost.description}</span>
                </div>
                <div className="cost-included-badge">Wliczone</div>
              </div>
            ))}
            {parkingSpots > 0 && parkingIncluded && (
              <div className="cost-item included">
                <div className="cost-description">
                  <span className="cost-name">Miejsce postojowe ({parkingSpots})</span>
                </div>
                <div className="cost-included-badge">Wliczone</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Total summary */}
      <div className="costs-total">
        <div className="total-row">
          <span>Cena mieszkania:</span>
          <span>{formatPLN(propertyPrice)}</span>
        </div>
        <div className="total-row">
          <span>Koszty obowiązkowe:</span>
          <span>+ {formatPLN(totalMandatoryCosts)}</span>
        </div>
        <div className="total-separator"></div>
        <div className="total-row final">
          <span>Całkowity koszt (bez opcjonalnych):</span>
          <span className="final-amount">{formatPLN(totalWithMandatory)}</span>
        </div>
      </div>
    </div>
  );
}

export default AdditionalCosts;